/*
 * Compare Media / SDP.
 *
 * These functions aim to determine how a Webrtc operation changed a
 *    PeerConnection's (local) SDP. It does this by comparing two SDPs from the
 *    same PeerConnection, one from before and one from after the operation.
 * The output (a `MediaDiff` object) is a description of how each media
 *    section of the SDP was changed by the operation.
 *
 * This file examines the SDP/media from a webrtc/SDP level, not from a call/SDK
 *    level.
 */

// Other plugins.
import { logManager } from '../../logs'

// Libraries.
import sdpTransform from 'sdp-transform'

const log = logManager.getLogger('SDP')

/**
 * Description of how a media line changes from one SDP to the next.
 * @typedef {string} MediaTransition
 */

/**
 * The possible values for media transitions.
 * @enum {MediaTransition}
 */
export const MEDIA_TRANSITIONS = {
  START: 'START',
  STOP: 'STOP',
  SAME: 'SAME'
}

/**
 * Compare two SDPs to determine the differences in their media sections.
 * The SDPs need to have been created by the same source.
 * @method compareSdp
 * @param  {string}  prevSdpString A previous SDP.
 * @param  {string}  sdpString     A new SDP.
 * @param  {boolean} [isNaive=false] Whether to match media sections naively.
 * @return {MediaDiff}
 */
export default function compareMedia (prevSdpString, sdpString, isNaive = false) {
  // Parse the SDP string into an SDP object for easier handling.
  const prevSdp = sdpTransform.parse(prevSdpString)
  const sdp = sdpTransform.parse(sdpString)

  // Take out only the parts of the SDP that we are interested in.
  //    Each "media" relates to a media section of the SDP.
  const prevMedia = summarizeMedia(prevSdp)
  const currMedia = summarizeMedia(sdp)

  let sortedMedia
  if (isNaive) {
    // Match the medias naively, instead of guaranteeing they are
    //    the same media section.
    sortedMedia = naiveMatchMedias(prevMedia, currMedia)
  } else {
    sortedMedia = matchMedias(prevMedia, currMedia)
  }

  // Media found in the previous SDP but not the latest SDP were removed.
  const removed = sortedMedia.prevUnmatched
  // Media found in the latest SDP but not the previous SDP were added.
  const added = sortedMedia.currUnmatched

  // Media found in both SDPs may have been changed.
  const changed = []
  const unchanged = []

  sortedMedia.matched.forEach(({ previous, current }) => {
    const changes = compareSummary(previous, current)

    if (changes.sending === MEDIA_TRANSITIONS.SAME && changes.receiving === MEDIA_TRANSITIONS.SAME) {
      // No changes.
      unchanged.push(previous)
    } else {
      // Changes. Include both the media summary and the changes found.
      /**
       * Description of how a media line changed, with the latest summary.
       * @typedef  {Object}       ChangedMedia
       * @property {MediaSummary} media        The new summary of the media section.
       * @property {MediaChanges} changes      The changes that occurred to the media.
       */
      changed.push({ media: current, changes })
    }
  })

  /**
   * The computed differences of media sections between two SDPs.
   * @typedef  {Object}              MediaDiff
   * @property {Array<MediaSummary>} added     Media sections that are new in the latest SDP.
   * @property {Array<MediaSummary>} removed   Media sections that are missing in the latest SDP.
   * @property {Array<ChangedMedia>} changed   Media changes between the two SDPs.
   * @property {Array<MediaSummary>} unchanged Media sections that remained the same.
   */
  return {
    added,
    removed,
    changed,
    unchanged
  }
}

/**
 * Reduce the media information in the SDP to only what we will use.
 * The SDP object provided as input is assumed to use standard values.
 *
 * Only supports SDPs where there is only one track per media section.
 *    eg. Plan-B when there is max 1 track per media type, and Unified-Plan (?).
 *
 * @method summarizeMedia
 * @param  {Object} sdp The full SDP object.
 * @return {Array<MediaSummary>} Summarized media sections from the SDP.
 */
export function summarizeMedia (sdp) {
  const sdpMedia = []
  sdp.media.forEach((media, index) => {
    /*
     * Generate/Find a unique ID for this media section. This will be used to
     *    compare against another SDP's media sections to find matching
     *    sections.
     */
    const sectionId = media.mid
    // In case the sectionId is undefined, take note of the index of the media section.
    const sectionIndex = index

    if (media.ssrcGroups && media.ssrcGroups.length > 1) {
      /*
       * All of the logic in this file assumes that a media section only has 1
       *    track. If there are more than one, it means that we are using
       *    Plan-B with more than 1 track of the same media type, which is not
       *    something that we want to support.
       */
      log.debug(`Media section contains multiple ssrc groups. Summary for ${sectionId} will be wrong.`)
    }

    // If the media section doesn't have a direction, then use the session-level direction.
    const mediaDirection = media.direction || sdp.direction

    // Whether this media section is sending and/or receiving.
    let willSend
    let willReceive

    if (mediaDirection) {
      willSend = mediaDirection.includes('send')
      willReceive = mediaDirection.includes('recv')
    } else {
      // If there is no media direction attribute at both the media-level and
      //    session-level, then it is to be assumed the direction is "sendrecv".
      // Ref: RFC 4566, page 26: https://tools.ietf.org/html/rfc4566
      willSend = true
      willReceive = true
    }

    let mediaId, trackId
    /*
     * If the media section is sending, then it has information about the track
     *    that it is sending.
     *
     * Note: The media section will never have information about a track it is
     *    receiving (ie. willReceive), because the SDP only describes the
     *    "local" PeerConnection's situation.
     */
    if (willSend) {
      /**
       * The SDP should either have a `msid` property (a=msid line) or `ssrc`
       *    lines with the same information (`a=ssrc:... label:...` and
       *    `a=ssrc:... mslabel:...`).
       * If it has nethier, then the SDP says it wants to send a track, but it
       *    has no track to send.
       */
      if (media.msid) {
        ;[mediaId, trackId] = media.msid.split(' ')
      } else if (media.ssrcs) {
        mediaId = media.ssrcs.find(ssrc => ssrc.attribute === 'mslabel').value
        trackId = media.ssrcs.find(ssrc => ssrc.attribute === 'label').value
      }
    } else {
      // This media section is not sending a track, so there is no information.
      mediaId = undefined
      trackId = undefined
    }

    /**
     * Summarized information about a media section from an SDP.
     * @typedef  {Object}  MediaSummary
     * @property {string}  sectionId    A unique identifier for the media section.
     * @property {string}  sectionIndex The index of the media section in the SDP.
     * @property {string}  type         The type of media line.
     * @property {boolean} willSend     Whether the media will send data.
     * @property {boolean} willReceive  Whether the media will receive data.
     * @property {string}  mediaId      The MediaStream ID of the media.
     * @property {string}  trackId      The MediaStreamTrack ID of the media.
     */
    const summary = {
      sectionId,
      sectionIndex,
      type: media.type,
      willSend,
      willReceive,
      mediaId,
      trackId
    }

    sdpMedia.push(summary)
  })
  return sdpMedia
}

/**
 * Matches MediaSummary objects between two lists of media.
 *    Finds matches between the lists based on the media's sectionId.
 *    If sectionId is undefined (rare cases), matches based on stream/track Ids.
 * Returns a list of matching medias, and a list of unmatched medias for both
 *    previous and current lists.
 * @param  {Array<MediaSummary>} prevList List of previous media summaries.
 * @param  {Array<MediaSummary>} currList List of current media summaries.
 * @return {Object} Three lists of matched, prevUnmmatched, and currUnmatched.
 */
export function matchMedias (prevList, currList) {
  // Redeclare these lists so we don't modify the originals.
  const prev = [...prevList]
  const curr = [...currList]

  const prevUnmatched = []
  let currUnmatched = []
  const matched = []

  // Function for matching a single media object to another object from a list.
  //    Matches by sectionId.
  const findMatchingSectionId = function (prevM, mediaList) {
    return mediaList.findIndex(media => media.sectionId === prevM.sectionId)
  }
  // Function for matching a single media object to another object from a list.
  //    Matches by media/track ID.
  const findMatchingMediaIds = function (prevM, mediaList) {
    return mediaList.findIndex(media => {
      return (
        // One or both of the sectionIds should be undefined, otherwise we shouldn't
        //    be matching with the media/track ID.
        (typeof prevM.sectionId === 'undefined' || typeof media.sectionId === 'undefined') &&
        prevM.mediaId === media.mediaId &&
        prevM.trackId === media.trackId
      )
    })
  }

  /*
   * Step 1:
   * Iterate over previous medias that have a defined sectionId.
   *    First try to find a matching current media with a sectionId.
   *    If none, try to find a matching current media with the same media IDs.
   *    If still none, then consider the prev media to be unmatched.
   */
  prev
    .filter(m => ['number', 'string'].includes(typeof m.sectionId))
    .forEach(prevM => {
      const currIndex = findMatchingSectionId(prevM, curr)
      if (currIndex >= 0) {
        // If there's a matching sectionId, add the two medias to the matched list.
        //    Remove currM from its list to ensure nothing else can match with it.
        const [currM] = curr.splice(currIndex, 1)
        matched.push({ previous: prevM, current: currM })
      } else {
        const currIndex = findMatchingMediaIds(prevM, curr)
        if (currIndex >= 0) {
          // If there's a matching media IDs, add the two medias to the matched list.
          //    Remove currM from its list to ensure nothing else can match with it.
          const [currM] = curr.splice(currIndex, 1)
          matched.push({ previous: prevM, current: currM })
        } else {
          // If a match still wasn't found, then consider prevM unmatched.
          prevUnmatched.push(prevM)
        }
      }
    })

  /*
   * Step 2:
   * Iterate over previous medias that do not have a defined sectionId.
   * Media with sectionIds were given priority since that should be 99% of scenarios.
   *    Try to find a matching current media with the same media IDs.
   *    If none, then consider the prev media to be unmatched.
   */
  prev
    .filter(m => typeof m.sectionId === 'undefined')
    .forEach(prevM => {
      const currIndex = findMatchingMediaIds(prevM, curr)
      if (currIndex >= 0) {
        // If there's a matching media IDs, add the two medias to the matched list.
        //    Remove currM from its list to ensure nothing else can match with it.
        const [currM] = curr.splice(currIndex, 1)
        matched.push({ previous: prevM, current: currM })
      } else {
        // If a match wasn't found, then consider prevM unmatched.
        prevUnmatched.push(prevM)
      }
    })

  /*
   * Step 3:
   * Consider all remaining medias in the current list to be unmatched.
   *    All matched currMedia should have been removed from the list when it was matched.
   */
  currUnmatched = curr

  return {
    prevUnmatched,
    currUnmatched,
    matched
  }
}

/**
 * Matches MediaSummary objects between two lists of media.
 *
 * Matches them based on their index in the lists. That's it.
 * This works under the assumption that the order of the media sections
 *    will never change, which is generally true. This may not be true
 *    in "non-basic" media scenarios (ie. more than two media sections)
 *    if a PeerConnection needs to be recreated.
 * This will only work if only comparing SDPs, and not using the
 *    Transceivers that represent the media sections.
 * @param  {Array<MediaSummary>} prevList List of previous media summaries.
 * @param  {Array<MediaSummary>} currList List of current media summaries.
 * @return {Object} Three lists of matched, prevUnmmatched, and currUnmatched.
 */
export function naiveMatchMedias (prevList, currList) {
  // Redeclare these lists so we don't modify the originals.
  const prev = [...prevList]
  const curr = [...currList]

  const prevUnmatched = []
  const currUnmatched = []
  const matched = []

  const longerLength = prev.length <= curr.length ? curr.length : prev.length

  // Loop enough times to cover which media list is longer.
  for (let i = 0; i < longerLength; i++) {
    if (prev[i] === undefined) {
      // If the prev media at this index is undefined, then the curr
      //    media at this index is currUnmatched.
      currUnmatched.push(curr[i])
    } else if (curr[i] === undefined) {
      // Same thing other way around.
      prevUnmatched.push(prev[i])
    } else {
      // Else both prev/curr have a media at this index. Match them.
      matched.push({ previous: prev[i], current: curr[i] })
    }
  }

  return {
    prevUnmatched,
    currUnmatched,
    matched
  }
}

/**
 * Compare two summarized Media section objects to determine how it changed.
 * @method compareSummary
 * @param  {MediaSummary} prevMedia The previous media summary.
 * @param  {MediaSummary} currMedia The current media summary.
 * @return {MediaChanges} How the media directions changed from prevMedia to currMedia.
 */
export function compareSummary (prevMedia, currMedia) {
  /**
   * Description of changes to a media line.
   * @typedef  {Object} MediaChanges
   * @property {MediaTransition} sending   The change in the media sending data.
   * @property {MediaTransition} receiving The change in the media receiving data.
   */
  const change = {
    sending: undefined,
    receiving: undefined
  }

  if (prevMedia.willSend !== currMedia.willSend) {
    if (currMedia.willSend) {
      // Started sending.
      change.sending = MEDIA_TRANSITIONS.START
    } else {
      // Stopped sending.
      change.sending = MEDIA_TRANSITIONS.STOP
    }
  } else {
    // No change.
    change.sending = MEDIA_TRANSITIONS.SAME
  }

  if (prevMedia.willReceive !== currMedia.willReceive) {
    if (currMedia.willReceive) {
      // Started receiving.
      change.receiving = MEDIA_TRANSITIONS.START
    } else {
      // Stopped receiving.
      change.receiving = MEDIA_TRANSITIONS.STOP
    }
  } else {
    // No change.
    change.receiving = MEDIA_TRANSITIONS.SAME
  }

  return change
}
